import { AuthStatus } from 'constants/auth';
import { useScrollDirection } from 'hooks/useScrollDirection';
import { useViewer } from 'hooks/useViewer';
import FilterIcon from 'svg/FilterIcon';
import LoadSpinner from 'svg/LoadSpinner';
import MapIcon from 'svg/Map';
import classNames from 'styles/utils/classNames';

interface Props {
  isOpenMap: boolean;
  onToggleMap: () => void;
  isInView: boolean;
  onOpenFilters?: () => void;
  isLoading?: boolean;
  loadingText?: string;
}

export default function MapToggleBar({
  isOpenMap,
  onToggleMap,
  isInView,
  onOpenFilters,
  isLoading = false,
  loadingText,
}: Props) {
  const scrollDirection = useScrollDirection();
  const viewer = useViewer();
  // The TabBar only renders for logged-in users and is hidden at md+ via CSS,
  // so the slide-away only applies when logged in (here) and below md (max-md variant).
  const isTabBarVisible = viewer.status === AuthStatus.User;

  return (
    <div
      className={classNames(
        'fixed bottom-8 right-1/2 z-10 flex h-11 translate-x-1/2 transform gap-6 rounded-full bg-color-bg-darkmode-primary px-6 text-sm text-color-text-lightmode-invert transition-transform duration-300 ease-in-out lg:hidden',
        !isInView && 'hidden',
        !isTabBarVisible || (scrollDirection === 'up' && !isOpenMap)
          ? 'translate-y-0'
          : 'translate-y-0 max-md:translate-y-[6rem]',
          isTabBarVisible && 'max-md:bottom-[7rem]'
      )}
    >
      {isLoading ? (
        <div className="flex items-center justify-center">
          <LoadSpinner className="h-5 w-5 animate-spin" />
          <p className="ml-2 select-none whitespace-nowrap">{loadingText}</p>
        </div>
      ) : (
        <>
          <div
            className="flex cursor-pointer items-center justify-center"
            onClick={onToggleMap}
          >
            <MapIcon className="h-5 w-5 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
            <p className="ml-2 select-none whitespace-nowrap">
              {isOpenMap ? 'Hide map' : 'Show map'}
            </p>
          </div>

          {onOpenFilters && (
            <>
              <div className="hidden h-full w-[1px] bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary max-lg:block" />

              <div
                className="hidden cursor-pointer items-center justify-center max-lg:flex"
                onClick={onOpenFilters}
              >
                <p className="mr-2 select-none whitespace-nowrap">Filters</p>
                <FilterIcon className="h-4 w-4 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
